import type { HostedFields } from 'braintree-web';
import _ from 'lodash';
import Q from 'q';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import createStore from 'vendor/cnpm/fluxible.v0-4/addons/createStore';

import logger from 'js/app/loggerSingleton';
import user from 'js/lib/user';

import { UserPreferenceKeys } from 'bundles/account-settings/constants/UserPreferences';
import type { PaymentWalletSettings } from 'bundles/account-settings/types/Payment';
import type { ThreeDSecureSettings } from 'bundles/account-settings/types/ThreeDSecureSettings';
import { setUserPreference } from 'bundles/account-settings/utils/UserPreferenceUtils';
import PaymentWalletsV1 from 'bundles/naptimejs/resources/paymentWallets.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import executeMutation from 'bundles/naptimejs/util/executeMutation';
import type { TokenizeCardRequest, TokenizeHostedFieldsRequest } from 'bundles/payments-common/common/types';
import {
  threeDSecureVerifyCard,
  tokenizeCard,
  tokenizeHostedFields,
} from 'bundles/payments-common/utils/braintreeClientUtils';
import PaymentProcessorTypes from 'bundles/payments/common/PaymentProcessorTypes';
import { fakeValidNonce } from 'bundles/payments/common/constants';
import type { BillingAddress } from 'bundles/payments/common/types';
import { getErrorMessageForPaymentProcessorCode } from 'bundles/payments/components/errorMessages';
import { isValidCCNumber } from 'bundles/payments/lib/validate';
import { getIsThreeDSecureEnabled } from 'bundles/payments/utils/threeDSecureUtils';

import _t from 'i18n!nls/payments';

const { PAYMENT, STORE_CREDIT_CARD } = UserPreferenceKeys;

const isNumber = (n: $TSFixMe) => !Number.isNaN(n);

const EnterCardFormStore = createStore({
  storeName: 'EnterCardFormStore',

  initialize() {
    this.country = null;
    this.zipcode = null;

    // Client token that we use to connect to braintree servers. This comes from /api/paymentProcessorClientTokens.v1
    this.braintreeClientToken = null;

    // Nonce that we get from braintree servers after we tokenize the card.
    this.creditCardNonce = null;

    const cardholderName = user.get().full_name;
    if (cardholderName) {
      this.creditCardData = {
        cardholderName,
      };
    } else {
      this.creditCardData = {};
    }

    this.activeWalletId = null;

    this.storeToWalletSuccess = null;
    this.storeToWalletErrorMessage = null;

    this.threeDSecureSettings = {};
  },

  hasLoaded() {
    return true;
  },

  isCreditCardValid(): boolean {
    if (this.braintreeHostedFields) {
      const { fields } = this.braintreeHostedFields.getState();
      return fields.number.isValid && fields.expirationDate.isValid && fields.cvv.isValid;
    } else {
      const { ccNumber, expirationMonth, expirationYear, cvv } = this.creditCardData;
      return !!(isValidCCNumber(ccNumber) && expirationMonth && expirationYear && cvv && isNumber(cvv));
    }
  },

  isFormValid(): boolean {
    const { cardholderName } = this.creditCardData;

    return !!(this.isValidLocation() && this.isCreditCardValid() && this.braintreeClientToken && cardholderName);
  },

  isValidLocation() {
    return !!(this.country === 'US' ? this.zipcode && this.country : this.country);
  },

  isValidCountryLocation() {
    return !!this.country;
  },

  getPaymentWallets() {
    const store = this.getNaptimeStore();

    if (!store) {
      return [];
    }

    const wallets = store.getDataForClient(PaymentWalletsV1.me());
    const braintreeWallets = _.filter(
      wallets,
      (wallet) => wallet.isValid && wallet.paymentProcessorId === PaymentProcessorTypes.braintree
    );

    return braintreeWallets;
  },

  getBillingInformation() {
    if (this.isValidCountryLocation()) {
      return {
        billingCountry: this.country,
        billingZipcode: this.zipcode,
      };
    }
    return undefined;
  },

  getFormattedBillingAddress() {
    if (this.isValidCountryLocation()) {
      let billingAddress: BillingAddress = { country: this.country };
      if (this.country === 'US') {
        billingAddress = { ...billingAddress, zipcode: this.zipcode };
      }
      if (this.country === 'CA') {
        billingAddress = { ...billingAddress, postalCode: this.zipcode };
      }
      return billingAddress;
    }
    return undefined;
  },

  getCreditCardData() {
    return this.creditCardData;
  },

  getFormattedBillingInfo() {
    return {
      billingAddress: { ...this.getFormattedBillingAddress() },
      creditCard: { ...this.creditCardData, cardNumber: this.creditCardData?.ccNumber },
    };
  },

  getPaymentWalletId() {
    return this.activeWalletId;
  },

  getNaptimeStore() {
    return this.dispatcher && this.dispatcher.getStore('NaptimeStore');
  },

  getBraintreeHostedFields(): HostedFields | undefined {
    return this.braintreeHostedFields;
  },

  checkAndVerifyCard() {
    const { amount, paymentWalletNonce }: ThreeDSecureSettings = this.threeDSecureSettings;
    const isThreeDSecureEnabled = getIsThreeDSecureEnabled(null, true);

    // Skip verification if 3DS is not enabled or there's no recurring amount tied to the wallet to verify
    if (!isThreeDSecureEnabled || typeof amount !== 'string' || parseInt(amount, 10) <= 0) {
      return Q({});
    } else if (!paymentWalletNonce) {
      throw new Error('Unable to run 3D Secure verification due to missing configuration');
    }

    const threeDSecureRequest = {
      nonce: paymentWalletNonce,
      amount,
    };

    return threeDSecureVerifyCard(this.braintreeClientToken, threeDSecureRequest).then((response) => {
      const { nonce } = response;
      this.creditCardNonce = nonce;
    });
  },

  storeCreditCardToWalletPromise({ useSandbox = undefined, recaptchaToken }: PaymentWalletSettings = {}) {
    const createPaymentWalletRequest = Object.assign(
      {
        nonce: useSandbox ? fakeValidNonce : this.creditCardNonce,
        paymentProcessorId: useSandbox ? PaymentProcessorTypes.braintreeSandbox : PaymentProcessorTypes.braintree,
        userId: user.get().id,
        useSandbox,
        recaptchaToken,
      },
      this.getBillingInformation()
    );

    const existingPaymentWalletId = this.getPaymentWalletId();

    const client = existingPaymentWalletId
      ? PaymentWalletsV1.update(existingPaymentWalletId, createPaymentWalletRequest)
      : PaymentWalletsV1.create(createPaymentWalletRequest);

    return this.executeMutationWithClient(client);
  },

  executeMutationWithClient(client: $TSFixMe) {
    return executeMutation(client, this.getNaptimeStore()).catch((error: $TSFixMe) => {
      throw error;
    });
  },

  tokenizeCardPromise() {
    const { cardholderName, ccNumber, expirationMonth, expirationYear, cvv } = this.creditCardData;
    const { billingCountry, billingZipcode } = this.getBillingInformation();

    const tokenizeCardRequest: TokenizeCardRequest = {
      number: ccNumber,
      cardholderName,
      cvv,
      expirationMonth,
      expirationYear,
      billingCountry,
      billingZipcode,
    };

    return tokenizeCard(tokenizeCardRequest, this.braintreeClientToken).then((cardNonce) => {
      this.creditCardNonce = cardNonce.nonce;
      return cardNonce.nonce;
    });
  },

  tokenizeHostedFieldsPromise() {
    const { cardholderName } = this.creditCardData;
    const { billingCountry, billingZipcode } = this.getBillingInformation();

    const tokenizeHostedFieldsRequest: TokenizeHostedFieldsRequest = {
      cardholderName,
      billingCountry,
      billingZipcode,
    };

    return tokenizeHostedFields(tokenizeHostedFieldsRequest, this.braintreeHostedFields).then((payload) => {
      this.creditCardNonce = payload.nonce;
      return payload.nonce;
    });
  },

  didSuccessfullyStoreToWallet() {
    return !!this.storeToWalletSuccess;
  },

  getStoreToWalletErrorMessage() {
    return this.storeToWalletErrorMessage;
  },
});

EnterCardFormStore.handlers = {
  UPDATE_COUNTRY(country: $TSFixMe) {
    this.country = country;
    this.emitChange();
  },

  UPDATE_ZIPCODE(zipcode: $TSFixMe) {
    this.zipcode = zipcode;
    this.emitChange();
  },

  SET_ACTIVE_WALLET_ID(walletId: $TSFixMe) {
    this.activeWalletId = walletId;
    this.emitChange();
  },

  SET_CREDIT_CARD_DATA(creditCardData: $TSFixMe) {
    this.creditCardData = Object.assign(this.creditCardData, creditCardData);
    this.emitChange();
  },

  SET_BRAINTREE_CLIENT_TOKEN(braintreeClientToken: $TSFixMe) {
    this.braintreeClientToken = braintreeClientToken;
    this.emitChange();
  },

  SET_THREE_D_SECURE_SETTINGS(settings: $TSFixMe) {
    this.threeDSecureSettings = Object.assign(this.threeDSecureSettings, settings);
    this.emitChange();
  },

  STORE_CREDIT_CARD_TO_WALLET(settings: PaymentWalletSettings) {
    if (!this.braintreeClientToken && !this.braintreeHostedFields) {
      logger.error('Unable to store credit card to wallet without braintree client token or hosted fields');
      return;
    }

    this.storeToWalletSuccess = false;
    this.storeToWalletErrorMessage = null;
    this.emitChange();

    // Ensure that store credit card preference is synced
    setUserPreference(PAYMENT, { [STORE_CREDIT_CARD]: true });
    Q()
      .then(() => (this.braintreeHostedFields ? this.tokenizeHostedFieldsPromise() : this.tokenizeCardPromise()))
      .then(() => this.checkAndVerifyCard())
      .then(() => this.storeCreditCardToWalletPromise(settings))
      .then((res) => {
        if (res && res.failure) {
          throw new Error('Error storing credit card to wallet');
        } else {
          this.storeToWalletSuccess = true;
          this.storeToWalletErrorMessage = null;
        }
      })
      .fail((err) => {
        const resp = err && err.responseJSON;
        const PROCESSOR_CODE_REGEX = /\((\d{4})\)/;
        const regexMatch = resp && resp.message && resp.message.match(PROCESSOR_CODE_REGEX);
        const errorCode = (regexMatch && regexMatch[1]) || (resp && resp.details && resp.details.processorResponseCode);

        logger.error(`Failed storing credit card with error: ${resp && resp.message}`);
        this.storeToWalletSuccess = false;
        this.storeToWalletErrorMessage =
          getErrorMessageForPaymentProcessorCode(errorCode) ||
          _t('We encountered an error while storing the credit card.'); // eslint-disable-line no-restricted-syntax
      })
      .finally(() => {
        this.emitChange();
      })
      .done();
  },

  SET_BRAINTREE_HOSTED_FIELDS(braintreeHostedFields: HostedFields) {
    this.braintreeHostedFields = braintreeHostedFields;
    this.emitChange();
  },

  CLEAR_BRAINTREE_HOSTED_FIELDS() {
    delete this.braintreeHostedFields;
    this.emitChange();
  },
};

export default EnterCardFormStore;
